import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Eye, Trash } from "@phosphor-icons/react";

const STATUSES = ["new", "read", "replied", "archived"];

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = filter !== "all" ? { status: filter } : {};
    const { data } = await api.get("/admin/inquiries", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]);

  const openView = async (it) => {
    setSelected(it);
    if (it.status === "new") {
      try { await api.put(`/admin/inquiries/${it.id}`, { status: "read" }); load(); } catch {}
    }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/inquiries/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    try { await api.delete(`/admin/inquiries/${id}`); setSelected(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader title="Inquiries" subtitle="Contact form submissions from the public site." />
      <div className="p-8 space-y-4">
        <div className="max-w-xs">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="inq-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="inquiries-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
              <tr>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Subject</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Received</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {items.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-[#4B5563]">No inquiries.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} data-testid={`inq-row-${it.id}`} className={it.status === "new" ? "bg-[#C2410C]/[0.02]" : ""}>
                  <td className="px-6 py-3 font-medium text-[#1F2937]">{it.name}</td>
                  <td className="px-6 py-3 text-[#4B5563]">{it.email}</td>
                  <td className="px-6 py-3 text-[#4B5563] max-w-xs truncate">{it.subject || "—"}</td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${
                      it.status === "new" ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"
                    }`}>{it.status}</span>
                  </td>
                  <td className="px-6 py-3 text-[#4B5563] text-xs">{new Date(it.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => openView(it)} data-testid={`view-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><Eye size={16} /></button>
                    <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="inq-dialog">
          <DialogHeader><DialogTitle>{selected?.subject || "Inquiry"}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-[#4B5563]">From</div>
                <div className="col-span-2">{selected.name} &lt;{selected.email}&gt;</div>
                {selected.phone && <><div className="text-[#4B5563]">Phone</div><div className="col-span-2">{selected.phone}</div></>}
                <div className="text-[#4B5563]">Received</div>
                <div className="col-span-2 text-xs">{new Date(selected.created_at).toLocaleString()}</div>
              </div>
              <div className="border-t border-[#E2E8F0] pt-3">
                <div className="text-xs text-[#4B5563] uppercase tracking-[0.18em] mb-2">Message</div>
                <p className="whitespace-pre-wrap text-[#1F2937]">{selected.message}</p>
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                {STATUSES.map((s) => (
                  <Button
                    key={s}
                    onClick={() => setStatus(selected.id, s)}
                    variant={selected.status === s ? "default" : "outline"}
                    size="sm"
                    data-testid={`set-${s}`}
                    className={selected.status === s ? "bg-[#C2410C] hover:bg-[#9A3412]" : ""}
                  >
                    Mark {s}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
